import { useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import { Async } from '../components/States.js';
import { Dd } from '../components/Dropdown.js';
import { authChartHref, authLow, usDay } from '../lib/authorizations.js';

/**
 * Reports surface — structured after the platform mockup
 * (legacy/EHR-7-30-V27.html · renderReportsModal): a tabbed report workspace with
 * a Standard view (pipeline funnel + overview cards) and a Custom builder.
 *
 * The mockup also carries Leaderboard / Win-Loss / Velocity / Cohort / Time&Pay
 * views. Those need per-user activity logs, stage-transition history and timesheet
 * data the backend does not yet track, so they are shown as an explicit
 * backend-pending notice rather than faked.
 */

interface ReportResp {
  kind: string;
  rows: { label: string; value: number | string }[];
}
interface Stage {
  id: string;
  name: string;
  position: number;
}

const LABELS: Record<string, string> = {
  'contacts-by-category': 'Contacts by category',
  'contacts-by-stage': 'Contacts by stage',
  'appointments-by-status': 'Appointments by status',
  outstanding: 'Outstanding balances',
  'auth-utilization': 'Authorization utilization',
  'win-loss': 'Win / Loss',
  cohort: 'Intake cohorts',
  velocity: 'Pipeline velocity',
};
const ANALYTICS = ['win-loss', 'velocity', 'cohort'] as const;
const OVERVIEW = [
  'contacts-by-category',
  'appointments-by-status',
  'outstanding',
  'auth-utilization',
] as const;
const BUILDER_KINDS = Object.keys(LABELS);

export function ReportsTab({
  onOpenContact,
}: {
  /** ehr#565: open a client's chart on a tab (the Authorizations report's client links). */
  onOpenContact?: (id: string, view?: string) => void;
} = {}) {
  const [tab, setTab] = useState<'standard' | 'custom' | 'timepay' | 'authorizations'>('standard');
  return (
    <div className="reports">
      <div className="reports-head">
        <h2>Reports</h2>
        <p className="muted">
          Leaderboards, win/loss, funnel, velocity, cohorts, and activity across all prospects.
        </p>
      </div>
      <div className="report-tabs">
        <button
          className={`report-tab ${tab === 'standard' ? 'active' : ''}`}
          onClick={() => setTab('standard')}
        >
          Standard reports
        </button>
        <button
          className={`report-tab ${tab === 'custom' ? 'active' : ''}`}
          onClick={() => setTab('custom')}
        >
          Custom report builder
        </button>
        <button
          className={`report-tab ${tab === 'timepay' ? 'active' : ''}`}
          onClick={() => setTab('timepay')}
        >
          Time &amp; Pay
        </button>
        {/* ehr#565: every client's authorizations, used and remaining. */}
        <button
          className={`report-tab ${tab === 'authorizations' ? 'active' : ''}`}
          onClick={() => setTab('authorizations')}
        >
          Authorizations
        </button>
      </div>
      {tab === 'standard' ? (
        <StandardReports />
      ) : tab === 'custom' ? (
        <CustomBuilder />
      ) : tab === 'authorizations' ? (
        <AuthorizationsReport onOpenContact={onOpenContact} />
      ) : (
        <TimePayReport />
      )}
    </div>
  );
}

/** Time & Pay — payroll/timesheet reporting. Needs a time-tracking module that isn't
 *  part of the EHR yet, so this is an explicit backend-pending notice, not faked data. */
function TimePayReport() {
  return (
    <div className="card report-note">
      <h3>Time &amp; Pay</h3>
      <p className="muted">
        Payroll &amp; timesheet reporting (scheduled vs. actual hours, rates, gross pay, CSV/IIF
        export) needs a time-tracking module that isn&rsquo;t part of the EHR yet. It&rsquo;s shown
        here as its own tab to match the report workspace, rather than populated with placeholder
        numbers.
      </p>
    </div>
  );
}

interface AuditRow {
  id: string;
  actor: string;
}

/** Standard view — pipeline funnel, team-activity leaderboard, overview cards. */
function StandardReports() {
  const state = useFetch<{
    stages: Stage[];
    stageRows: ReportResp;
    audit: AuditRow[];
    analytics: ReportResp[];
    overview: ReportResp[];
  }>(
    () =>
      Promise.all([
        api.get<Stage[]>('/settings/stages'),
        api.get<ReportResp>('/reports/contacts-by-stage'),
        api.get<{ rows: AuditRow[] }>('/audit').then((r) => r.rows ?? []), // #61: /audit is now { rows, total }
        ...ANALYTICS.map((k) => api.get<ReportResp>(`/reports/${k}`)),
        ...OVERVIEW.map((k) => api.get<ReportResp>(`/reports/${k}`)),
      ]).then(([stages, stageRows, audit, ...rest]) => ({
        stages,
        stageRows,
        audit,
        analytics: rest.slice(0, ANALYTICS.length),
        overview: rest.slice(ANALYTICS.length),
      })),
    [],
  );

  return (
    <Async state={state}>
      {(d) => {
        const counts = new Map<string, number>();
        for (const a of d.audit) counts.set(a.actor, (counts.get(a.actor) ?? 0) + 1);
        const leaderboard = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
        return (
          <>
            <FunnelView stages={d.stages} rows={d.stageRows.rows} />

            <div className="card panel" style={{ marginTop: 18 }}>
              <div className="panel-head">
                <h3>Team activity</h3>
                <span className="muted">{d.audit.length} actions logged</span>
              </div>
              {leaderboard.length === 0 ? (
                <p className="muted" style={{ padding: '0 18px 18px' }}>
                  No activity recorded yet.
                </p>
              ) : (
                leaderboard.map(([actor, n], i) => (
                  <div key={actor} className="panel-item">
                    <span>
                      <strong style={{ color: 'var(--text-muted)', marginRight: 8 }}>
                        #{i + 1}
                      </strong>
                      {actor}
                    </span>
                    <strong>{n}</strong>
                  </div>
                ))
              )}
            </div>

            <div className="report-card-grid">
              {ANALYTICS.map((k, i) => (
                <MiniReport key={k} title={LABELS[k] ?? k} rows={d.analytics[i]?.rows ?? []} />
              ))}
            </div>

            <div className="report-card-grid">
              {OVERVIEW.map((k, i) => (
                <MiniReport key={k} title={LABELS[k] ?? k} rows={d.overview[i]?.rows ?? []} />
              ))}
            </div>
          </>
        );
      }}
    </Async>
  );
}

/** Real pipeline funnel: stages in order, counts, bar fills, step conversion. */
function FunnelView({
  stages,
  rows,
}: {
  stages: Stage[];
  rows: { label: string; value: number | string }[];
}) {
  const countOf = new Map(rows.map((r) => [r.label, Number(r.value) || 0]));
  const ordered = [...stages].sort((a, b) => a.position - b.position);
  const steps = ordered.map((s) => ({ name: s.name, n: countOf.get(s.name) ?? 0 }));
  const max = Math.max(1, ...steps.map((s) => s.n));
  const top = steps[0]?.n ?? 0;
  const total = steps.reduce((sum, s) => sum + s.n, 0);
  const DOT = ['#1a8a66', '#22a87e', '#3dbd94', '#0e7490', '#3b72d9', '#7c3aed'];

  return (
    <div className="card panel">
      <div className="panel-head">
        <h3>Pipeline funnel</h3>
        <span className="muted">{total} in pipeline</span>
      </div>
      {steps.length === 0 ? (
        <p className="muted" style={{ padding: '0 18px 18px' }}>
          No stages configured.
        </p>
      ) : (
        <div className="funnel-chart">
          {steps.map((s, i) => {
            const prev = i > 0 ? steps[i - 1]!.n : s.n;
            const stepConv = prev > 0 ? Math.round((s.n / prev) * 100) : 100;
            const overall = top > 0 ? Math.round((s.n / top) * 100) : 0;
            const drop = i > 0 && prev > 0 ? Math.round(((prev - s.n) / prev) * 100) : 0;
            const color = DOT[i % DOT.length]!;
            return (
              <div key={s.name}>
                {i > 0 && (
                  <div className="funnel-connector">
                    <span className="funnel-drop-pct">
                      {drop > 0 ? `↓ ${drop}% drop-off` : 'no drop-off'}
                    </span>
                  </div>
                )}
                <div className="funnel-row">
                  <div className="funnel-row-label">
                    <span className="funnel-stage-dot" style={{ background: color }} />
                    <span className="funnel-stage-name" title={s.name}>
                      {s.name}
                    </span>
                  </div>
                  <div className="funnel-row-bar">
                    <div className="funnel-bar-track">
                      <div
                        className="funnel-bar-fill"
                        style={{ width: `${Math.max((s.n / max) * 100, 4)}%`, background: color }}
                      />
                      <span className="funnel-bar-count">{s.n}</span>
                    </div>
                  </div>
                  <div className="funnel-row-conv">
                    <span className="funnel-conv-step">{i === 0 ? '100%' : `${stepConv}%`}</span>
                    <span className="funnel-conv-meta">
                      {i === 0 ? 'entered pipeline' : `${overall}% of top of funnel`}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MiniReport({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; value: number | string }[];
}) {
  return (
    <div className="card panel">
      <div className="panel-head">
        <h3>{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="muted">No data.</p>
      ) : (
        rows.map((r, i) => (
          <div key={i} className="panel-item">
            <span>{r.label}</span>
            <strong>{r.value}</strong>
          </div>
        ))
      )}
    </div>
  );
}

/** Custom builder — pick any aggregate, view it, export CSV (preserves the prior surface). */
function CustomBuilder() {
  const [kind, setKind] = useState('contacts-by-category');
  const state = useFetch<ReportResp>(() => api.get(`/reports/${kind}`), [kind]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2>Custom report</h2>
        <span className="spacer" style={{ flex: 1 }} />
        <div style={{ width: 220 }}>
          <Dd
            value={kind}
            onChange={setKind}
            ariaLabel="Report"
            options={BUILDER_KINDS.map((k) => ({ value: k, label: LABELS[k] ?? k }))}
          />
        </div>
        <a className="btn sm" href={`/api/v1/reports/${kind}?format=csv`} download>
          Export CSV
        </a>
      </div>
      <Async state={state} emptyWhen={(d) => d.rows.length === 0} emptyMessage="No data.">
        {(d) => (
          <table>
            <thead>
              <tr>
                <th>{LABELS[d.kind] ?? d.kind}</th>
                <th style={{ textAlign: 'right' }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.label}</td>
                  <td style={{ textAlign: 'right' }}>{r.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Async>
    </div>
  );
}

export interface AuthReportRow {
  contactId: string;
  clientName: string;
  authorizationId: string | null;
  payer: string | null;
  authNo: string | null;
  status: string | null;
  authorized: number | null;
  unitKind: 'sessions' | 'units' | null;
  used: number | null;
  remaining: number | null;
  startsOn: string | null;
  expiresOn: string | null;
}

/** The chart's labels for an authorization status (approved shows as Active there too). */
const AUTH_STATUS: Record<string, string> = {
  approved: 'Active',
  requested: 'Requested',
  denied: 'Denied',
  expired: 'Expired',
  exhausted: 'Exhausted',
};
const NONE = '__none';
const PAGE = 25;

/**
 * ehr#565 (AUTHORIZATIONS_REPORT_MOCK_10-20-V1) - Reporting > Authorizations: every active client,
 * one row per authorization, with Authorized (sessions or units), Used and Remaining as the chart
 * counts them (checked-out sessions). The client name opens the chart on its Authorizations tab.
 * Missing values show in red, as do authorizations running low.
 */
export function AuthorizationsReport({
  onOpenContact,
}: {
  onOpenContact?: (id: string, view?: string) => void;
}) {
  const state = useFetch<{ rows: AuthReportRow[] }>(() => api.get('/reports/authorizations'), []);
  const [q, setQ] = useState('');
  const [payer, setPayer] = useState('');
  const [status, setStatus] = useState('');
  const [applied, setApplied] = useState({ q: '', payer: '', status: '' });
  const [page, setPage] = useState(0);
  const rows = useMemo(() => state.data?.rows ?? [], [state.data]);
  const payers = useMemo(
    () =>
      [...new Set(rows.map((r) => r.payer).filter((x): x is string => !!x))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [rows],
  );
  const shown = useMemo(() => {
    const needle = applied.q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!needle ||
          r.clientName.toLowerCase().includes(needle) ||
          (r.authNo ?? '').toLowerCase().includes(needle)) &&
        (!applied.payer || r.payer === applied.payer) &&
        (!applied.status ||
          (applied.status === NONE ? !r.authorizationId : r.status === applied.status)),
    );
  }, [rows, applied]);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE));
  const at = Math.min(page, pages - 1);
  const slice = shown.slice(at * PAGE, at * PAGE + PAGE);
  const search = () => {
    setApplied({ q, payer, status });
    setPage(0);
  };
  const clear = () => {
    setQ('');
    setPayer('');
    setStatus('');
    setApplied({ q: '', payer: '', status: '' });
    setPage(0);
  };
  const miss = <span className="auth-report-miss">Missing</span>;

  return (
    <div className="card auth-report">
      <div className="auth-report-eyebrow">Reporting</div>
      <h2 style={{ margin: '0 0 14px' }}>Authorizations</h2>
      <div className="auth-report-filters">
        <input
          type="text"
          value={q}
          placeholder="Search client or auth number"
          aria-label="Search client or auth number"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') search();
          }}
        />
        <div className="auth-report-dd">
          <Dd
            value={payer}
            onChange={setPayer}
            ariaLabel="Payor"
            options={[
              { value: '', label: 'All Payors' },
              ...payers.map((p) => ({ value: p, label: p })),
            ]}
          />
        </div>
        <div className="auth-report-dd">
          <Dd
            value={status}
            onChange={setStatus}
            ariaLabel="Status"
            searchable={false}
            options={[
              { value: '', label: 'All Statuses' },
              ...Object.entries(AUTH_STATUS).map(([value, label]) => ({ value, label })),
              { value: NONE, label: 'No authorization' },
            ]}
          />
        </div>
        <span className="spacer" />
        <button type="button" className="btn" onClick={clear}>
          Clear
        </button>
        <button type="button" className="btn primary" onClick={search}>
          Search
        </button>
      </div>
      <Async state={state}>
        {() => (
          <>
            <div className="auth-report-wrap">
              <table className="auth-report-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Payor</th>
                    <th>Auth Number</th>
                    <th>Status</th>
                    <th className="num">Authorized</th>
                    <th className="num">Used</th>
                    <th className="num">Remaining</th>
                    <th>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {slice.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="muted">
                        No clients match.
                      </td>
                    </tr>
                  ) : (
                    slice.map((r) => (
                      <tr key={r.authorizationId ?? `none-${r.contactId}`}>
                        <td>
                          <a
                            className="auth-report-client"
                            href={authChartHref(r.contactId)}
                            onClick={(e) => {
                              // A plain click opens the chart here; Ctrl / Cmd click a new tab.
                              if (!onOpenContact || e.metaKey || e.ctrlKey || e.shiftKey) return;
                              e.preventDefault();
                              onOpenContact(r.contactId, 'authorizations');
                            }}
                          >
                            {r.clientName}
                          </a>
                        </td>
                        {!r.authorizationId ? (
                          <>
                            <td className="muted">—</td>
                            <td className="muted">No authorization on file</td>
                            <td className="muted">—</td>
                            <td className="num muted">—</td>
                            <td className="num muted">—</td>
                            <td className="num muted">—</td>
                            <td className="muted">—</td>
                          </>
                        ) : (
                          <>
                            <td>{r.payer ?? miss}</td>
                            <td>{r.authNo ?? miss}</td>
                            <td>{AUTH_STATUS[r.status ?? ''] ?? r.status ?? '—'}</td>
                            <td className="num">
                              {r.authorized != null
                                ? `${r.authorized} ${r.unitKind ?? ''}`.trim()
                                : miss}
                            </td>
                            <td className="num">{r.used ?? 0}</td>
                            <td className={`num${authLow(r) ? ' auth-report-low' : ''}`}>
                              {r.remaining ?? '—'}
                            </td>
                            <td>{r.expiresOn ? usDay(r.expiresOn) : miss}</td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="auth-report-foot">
              <div>
                {shown.length
                  ? `Showing ${at * PAGE + 1} to ${at * PAGE + slice.length} of ${shown.length}`
                  : 'Showing 0 of 0'}
              </div>
              {pages > 1 && (
                <div className="auth-report-pager">
                  <button
                    type="button"
                    aria-label="Previous page"
                    disabled={at === 0}
                    onClick={() => setPage(at - 1)}
                  >
                    ‹
                  </button>
                  {Array.from({ length: pages }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={i === at ? 'on' : ''}
                      onClick={() => setPage(i)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    aria-label="Next page"
                    disabled={at >= pages - 1}
                    onClick={() => setPage(at + 1)}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </Async>
    </div>
  );
}
